import { C } from "./theme.js";

// Loss to Lease was dropped as a scored metric (Sep 2026, FPM request):
// it distorted composite scores. It still appears on the income statement.
export const defaultWeights = { occupancy: 25, noiUnit: 35, leakage: 25, expRatio: 15 };
export const defaultBenchmarks = { occupancy: 93, noiUnit: 420, leakage: 5, expRatio: 50 };

export const metricMeta = {
  occupancy: { label: "Occupancy %", unit: "%", higherBetter: true, fmt: (v) => `${v.toFixed(1)}%` },
  noiUnit: { label: "NOI / Unit", unit: "$", higherBetter: true, fmt: (v) => `$${Math.round(v)}` },
  leakage: { label: "Revenue Leakage", unit: "%", higherBetter: false, fmt: (v) => `${v.toFixed(1)}%` },
  expRatio: { label: "Expense Ratio", unit: "%", higherBetter: false, fmt: (v) => `${v.toFixed(1)}%` },
};

// Drop any keys from saved settings that no longer correspond to a scored
// metric (e.g. the retired `ltl` entry), so stale weights don't dilute the
// composite and stale benchmarks don't linger in the UI.
export function pickScoredMetrics(obj) {
  if (!obj) return obj;
  return Object.fromEntries(Object.entries(obj).filter(([k]) => k in metricMeta));
}

// ResMan signs deduction lines negative when revenue is lost and positive
// when recoveries (collections on prior write-offs) exceed new losses. Net
// the three leakage lines with their signs intact so a recovery offsets
// leakage instead of being counted as more of it; never below zero.
export function netLeakage(badDebt, uncollectible, concessions) {
  const net = (badDebt || 0) + (uncollectible || 0) + (concessions || 0);
  return net < 0 ? -net : 0;
}

export function computeMetrics(p) {
  const gr = p.gross_rent?.a || 0;
  const occ = gr ? (1 - Math.abs(p.vacancies?.a || 0) / gr) * 100 : 0;
  const noiUnit = p.units ? (p.noi?.a || 0) / p.units : 0;
  const leakage = gr
    ? (netLeakage(p.bad_debt?.a, p.uncollectible?.a, p.concessions?.a) / gr) * 100
    : 0;
  const expRatio = p.total_income?.a ? (p.total_expense.a / p.total_income.a) * 100 : 0;

  const grB = p.gross_rent?.b || 0;
  const occB = grB ? (1 - Math.abs(p.vacancies?.b || 0) / grB) * 100 : 0;
  const noiUnitB = p.units && p.noi?.b ? p.noi.b / p.units : 0;
  const leakageB = grB
    ? (netLeakage(p.bad_debt?.b, p.uncollectible?.b, p.concessions?.b) / grB) * 100
    : 0;
  const expRatioB = p.total_income?.b ? (p.total_expense.b / p.total_income.b) * 100 : 0;

  return { occ, noiUnit, leakage, expRatio, occB, noiUnitB, leakageB, expRatioB };
}

export function scoreMetric(actual, target, higherBetter, spread = null) {
  if (target === 0) return 50;
  const sp = spread || target * 0.25;
  if (higherBetter) {
    if (actual >= target) return Math.min(100, 80 + ((actual - target) / sp) * 20);
    const pct = (target - actual) / sp;
    return Math.max(0, 80 - pct * 80);
  } else {
    if (actual <= target) return Math.min(100, 80 + ((target - actual) / sp) * 20);
    const pct = (actual - target) / sp;
    return Math.max(0, 80 - pct * 80);
  }
}

// ttmMetrics, when provided, lets the "trailing12" basis produce real targets
// instead of falling back to benchmarks.
export function computeScore(p, basis, benchmarks, weights, ttmMetrics = null) {
  const m = computeMetrics(p);
  // Only count weights for metrics that are actually scored.
  const totalW = Object.keys(metricMeta).reduce((s, k) => s + (weights[k] || 0), 0) || 1;

  let targets;
  if (basis === "budget") {
    targets = {
      occupancy: m.occB,
      noiUnit: m.noiUnitB,
      leakage: m.leakageB,
      expRatio: m.expRatioB,
    };
  } else if (basis === "trailing12" && ttmMetrics) {
    targets = {
      occupancy: ttmMetrics.occ,
      noiUnit: ttmMetrics.noiUnit,
      leakage: ttmMetrics.leakage,
      expRatio: ttmMetrics.expRatio,
    };
  } else {
    targets = {
      occupancy: benchmarks.occupancy,
      noiUnit: benchmarks.noiUnit,
      leakage: benchmarks.leakage,
      expRatio: benchmarks.expRatio,
    };
  }

  const components = {
    occupancy: { actual: m.occ, target: targets.occupancy, score: scoreMetric(m.occ, targets.occupancy, true, 8) },
    noiUnit: { actual: m.noiUnit, target: targets.noiUnit, score: scoreMetric(m.noiUnit, targets.noiUnit, true) },
    leakage: { actual: m.leakage, target: targets.leakage, score: scoreMetric(m.leakage, targets.leakage, false) },
    expRatio: { actual: m.expRatio, target: targets.expRatio, score: scoreMetric(m.expRatio, targets.expRatio, false) },
  };

  const composite =
    Object.entries(components).reduce((sum, [key, c]) => sum + c.score * (weights[key] || 0), 0) / totalW;
  return { composite: Math.round(composite), components };
}

export function scoreColor(s) {
  return s >= 80 ? C.score90 : s >= 65 ? C.score70 : s >= 45 ? C.score50 : C.score0;
}
export function scoreBg(s) {
  return s >= 80 ? C.greenBg : s >= 65 ? C.accentLight : s >= 45 ? C.amberBg : C.redBg;
}
export function scoreLabel(s) {
  return s >= 80 ? "Strong" : s >= 65 ? "Watch" : s >= 45 ? "Concern" : "Action Needed";
}

export const fmt = (v) => {
  if (v == null) return "—";
  const s = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(1)}K`;
  return `${s}$${a.toLocaleString()}`;
};

// Aggregate an array of monthly snapshots (shape = full property record with
// {a, b} fields) into a synthetic "trailing average" snapshot using the mean
// of actuals. Budget `b` values are dropped since TTM has no meaningful budget.
export function aggregateTTM(snapshots) {
  if (!snapshots || snapshots.length === 0) return null;
  const fields = [
    "gross_rent",
    "loss_to_lease",
    "vacancies",
    "concessions",
    "bad_debt",
    "uncollectible",
    "deductions",
    "net_rental",
    "other_income",
    "total_income",
    "total_expense",
    "noi",
    "capex",
    "debt_service",
    "net_income",
  ];
  const out = {};
  for (const f of fields) {
    const vals = snapshots.map((s) => s?.[f]?.a).filter((v) => typeof v === "number");
    out[f] = vals.length ? Math.round(vals.reduce((x, y) => x + y, 0) / vals.length) : 0;
  }
  return out;
}
